// @flow
import * as React from 'react';
import styled, {keyframes} from 'styled-components';
import {ButtonImage} from 'components/Image';
import {ButtonIconL, ButtonIconM, type PropsButton} from 'components/ButtonIcon';
import {IsDesktopAgent} from 'components/Responsive';
import {LocalText} from 'components/Localized';
import HueSlider from 'components/HueSlider';
import {useSessionState, useSessionDispatch} from 'components/Session';
import {ButtonSizeL} from 'components/Constants';
import Settings from 'config/Settings';

type PropsOptionButton = {
  onClick: () => void,
  active?: boolean,
  prefix: string,
  code: string,
};

export function OptionButton(props: PropsOptionButton): React.Element<any> {
  const filenameBase = `./icons/btn${props.prefix ? '_' : ''}${props.prefix}_${props.code}`;
  return (
    <ButtonIconL onClick={props.onClick} source={props.active ? `${filenameBase}_active.svg` : `${filenameBase}.svg`} sourceMobile={props.active ? `${filenameBase}_active.svg` : `${filenameBase}.svg`} />
  );
}

type SelectionButtonProps = {
  onClick?: () => void,
  code: string,
  prefix: string,
  active?: boolean,
  checked?: boolean,
};

export function SelectionButton(
  props: SelectionButtonProps
): React.Element<any> {
  const {onClick, code, prefix, ...other} = props;
  const filenameBase = `./${Settings.asset}/options_${prefix}_${code ?? ''}`;
  if (prefix === "option") {
    return <ButtonIconOption {...other} onClick={onClick} source={props.active ? (props.checked === true ? `${filenameBase}_active_checked.png` : `${filenameBase}_active.png`) : (props.checked === true ? `${filenameBase}_checked.png` : `${filenameBase}.png`)} />;
  }
  else if (prefix === "position") {
    return <ButtonIconL {...other} onClick={onClick} source={props.active ? `${filenameBase}_active.svg` : `${filenameBase}.svg`} />
  }
  return <ButtonIconL {...other} onClick={onClick} source={props.active ? `${filenameBase}_active.png` : `${filenameBase}.png`} />;
}

type SelectionsProps = {
  prefix: string,
  selected?: string,
  closing?: boolean,
  closingDirection: number,
  onChange?: (string, string) => void,
  dataHidden: boolean,
};

export function Selections(props: SelectionsProps): React.Element<any> {
  const sessionDispatch = useSessionDispatch();
  const sessionState = useSessionState();
  const selectedIdx = React.useMemo(() => sessionState.options[props.prefix] ?? 0, [sessionState.options, props.prefix]);
  const codes = React.useMemo(() => {
    return Settings[`${props.prefix}Codes`]?.[sessionState.modelIndex] ?? [];
  }, [props, sessionState]);
  const [selected, setSelected] = React.useState(codes[selectedIdx]);
  const [checked, setChecked] = React.useState(false);

  const handleSelected = React.useCallback((btn: string) => {
    //setSelected(btn);
    if (props.prefix === "option") {
      const nextChecked = !sessionState.options[btn];
      if (btn === "o03" && nextChecked && sessionState.options["o04"]) {
        sessionDispatch({type: 'SELECT_OPTION_DATA', data: false, option: "o04"});
      }
      if (btn === "o04" && nextChecked && sessionState.options["o03"]) {
        sessionDispatch({type: 'SELECT_OPTION_DATA', data: false, option: "o03"});
      }
      sessionDispatch({type: 'SELECT_OPTION_DATA', data: nextChecked, option: btn});
      setChecked(nextChecked)
    }
    sessionDispatch({type: 'SELECT_OPTION', data: btn, option: props.prefix});
    setSelected(btn);
    props.onChange?.(props.prefix, btn);
  }, [sessionDispatch, sessionState.options, props]);

  return (
    <SelectionsContainer dataClosing={props.closing} dataClosingDirection={props.closingDirection}>
      <SelectionsWrapper dataClosing={props.closing} dataHidden={props.dataHidden} dataScrollable={codes.length > 0} dataChecked={checked}>
        <div>
        <div>
        {codes.map((value) => (
          <SelectionButton
            key={value}
            prefix={props.prefix}
            code={value}
            active={selected === value}
            checked={sessionState.options[value]}
            onClick={() => handleSelected(value)}
          />
        ))}
        </div>
        </div>
      </SelectionsWrapper>
        {props.prefix && selected && <LocalText textKey="options_name_format" args={[`$options_${props.prefix}_${selected}_name`]} />}
    </SelectionsContainer>
  );
}

type PropsOptionsWrapper = {
  children: React.Node,
};

export function OptionsWrapper(
  props: PropsOptionsWrapper
): React.Element<any> {
  const {children, ...others} = props;
  return (
    <OptionsContainer {...others}>
      <div className="option-need-safearea">
        <OptionsDiv {...others}>{children}</OptionsDiv>
      </div>
    </OptionsContainer>
  );
}

type HueSelectionsProps = {
  closing?: boolean,
  dataClosingDirection: number
};

export function HueSelections(props: HueSelectionsProps): React.Element<any> {
  const sessionDispatch = useSessionDispatch();
  const sessionState = useSessionState();
  const initialHue = sessionState.options["ambient"] ?? 0;
  const [onOff, setOnOff] = React.useState(sessionState.options["ambientOn"] ?? true);

  const handleClick = React.useCallback(() => {
    const nextOnOff = !onOff;
    sessionDispatch({type: 'SELECT_OPTION_DATA', data: nextOnOff, option: "ambientOn"});
    setOnOff(nextOnOff)
  }, [sessionDispatch, onOff]);

  const handleChange = React.useCallback((value: number) => {
    sessionDispatch({type: 'SELECT_OPTION_DATA', data: value, option: "ambient"});
    if (!onOff) {
      handleClick();
    }
  }, [sessionDispatch, onOff, handleClick]);

  return (
    <SelectionsContainer dataClosing={props.closing} dataClosingDirection={props.dataClosingDirection}>
      <HueSliderWrapper>
        <HueSlider initialValue={initialHue} onChange={handleChange} />
        <ButtonIconM onClick={handleClick} source={onOff ? "./icons/btn_power_on.svg" : "./icons/btn_power_off.svg"} />
      </HueSliderWrapper>
      <IsDesktopAgent>
        <LocalText textKey="options_name_format" args={["$options_ambient_name"]} />
      </IsDesktopAgent>
    </SelectionsContainer>
  );
}

const SelectionsWrapper = styled.div`
  width: ${(props:any) => props.dataScrollable ? `${props.theme.layout.width}px` : undefined};
  overflow-x: scroll;
  overflow-y: hidden;

  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }

  pointer-events: ${(props: any) => (props.dataHidden || props.dataClosing) ? undefined : 'auto'};

  > div {
    display: inline-block;

    > div {
      display: flex;
      margin-bottom: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 8 : 6)}px;
      > button {
        margin-right: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 12 : 9)}px;
      }

      >:first-child {
        margin-left: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 12 : 9)}px;
      }
    }
  }
`;

const HueSliderWrapper = styled.div`
  display: flex;
  align-items: center;
  > div {
    display: inline-block;
    margin-right: ${(props: any) => props.theme.layout.scale * (props.theme.layout.sizeL ? 16 : 12)}px;
  }
`

const openAnimation = keyframes`
  0% { transform: translateX(100px); opacity: 0; }
  100% { transform: translateX(0px); opacity: 1;}
`

const closeAnimation = keyframes`
  0% { transform: translateX(0px); opacity: 1;}
  100% { transform: translateX(100px); opacity: 0;}
`

const openAnimationRight = keyframes`
  0% { transform: translateX(-100px); opacity: 0; }
  100% { transform: translateX(0px); opacity: 1;}
`

const closeAnimationRight = keyframes`
  0% { transform: translateX(0px); opacity: 1;}
  100% { transform: translateX(-100px); opacity: 0;}
`

const SelectionsContainer = styled.div`
  display: inline-block;
  text-align: center;
  margin-bottom: ${(props: any) => props.theme.layout.scale * (props.theme.layout.sizeL ? 16 : 16)}px;

  animation-name: ${(props: any) => props.dataClosing === undefined ? 'undefind' : (props.dataClosing ? (props.dataClosingDirection > 0 ? closeAnimation : closeAnimationRight) : (props.dataClosingDirection > 0 ? openAnimation : openAnimationRight))};
  animation-duration: 0.25s;
`;

const OptionsContainer: React.AbstractComponent<any> = styled.div`
  height: ${(props: any) => props.isMultiRow ? undefined : `${props.theme.layout.scale * (props.theme.layout.sizeL ? ButtonSizeL.desktop : ButtonSizeL.mobile)}px`};
  overflow-x: visible;
  overflow-y: hidden;
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }

  opacity: ${(props: any) => props.dataHidden ? 0 : undefined};
  transition: opacity ${(props: any) => props.theme.defaults.transitionDuration}s;

  pointer-events: ${(props: any) => props.dataHidden ? 'none' : 'auto'};
  * button {
    pointer-events: ${(props: any) => props.dataHidden ? 'none' : undefined};
  }

  > div {
    display: inline-block;
    margin: 0px ${(props: any) => props.theme.layout.scale * (props.theme.layout.sizeL ? 0 : 14)}px;
  }
`;

const OptionsDiv: React.AbstractComponent<any> = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${(props: any) => props.theme.layout.sizeL ? undefined : 'column'};

  > div {
    > button {
      margin-right: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 12 : 9)}px;
    }
    > div {
      margin-right: ${(props: any) => props.theme.layout.scale * (props.theme.layout.mobile ? 26 : 16)}px;
    }

    > :last-child {
      margin-right: 0px;
    }
  }
`;

const ButtonIconOption: React.AbstractComponent<PropsButton> = styled(ButtonImage)`
  width: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 100 : 75)}px;
  height: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 56 : 42)}px;
`;

export {OptionsContainer};
